import { useEffect, useState } from 'react';
import { COLUMN_TYPE_SLUGS } from '../../constants/active-column-constants.js';
import StatusButton from '../../utils/status/status_button.jsx';
import { fetchValueByDotOperator, isEmptyArray, isEmptyObject } from '../../utils/index.js';
import DisplayDateTime, { DisplayDate } from '../../utils/time/display_time.js';
import { Avatar, IF, Switch } from '../../common/index.js';
import { getActions } from '../../utils/actions-util/index.js';
import { ORDER_TYPE_ICONS, SOURCE_CLASS_NAMES } from '../../constants/generic-constants.js';
import { Flex, Tooltip } from 'antd';
import ChangeAssignee from '../../components/lead-detail/lead-tickets/ChangeAssignee.jsx';
import RenderMultipleUser from './RenderMultipleUser.js';
import RenderMultipleFilters from './RenderMultipleFilters.js';
import styles from './useTableColumns.module.scss';
import RenderProductsArray from './RenderProductsArray.jsx';
import DeactivateUserToggle from './DeactivateUserToggle.jsx';
import CopyToClipboard from 'common/copy-to-clipboard/CopyToClipboard.jsx';
import ColumnInfo from 'common/column-info/ColumnInfo.jsx';
import RenderHoverCopyText from './RenderHoverCopyText.jsx';
import RenderMultiPills from './RenderMultiPill.jsx';

const useTableColumns = ({
  activeColumns = [],
  onRowClick = () => {},
  fetch = () => {},
  actionsList = [],
  extraAction = () => {},
  extraActionDisabled = false,
  extraActionLoading = false,
  renderActionsDirectly = false,
}) => {
  const [tableColumns, setTableColumns] = useState([]);

  const getStatusColKey = (cellKey = '') => {
    if (cellKey === 'name') return 'status';
    return cellKey.replace('.name', '');
  };

  const getStatusColValue = (activeCol = {}, row = {}, columnKey = '') => {
    if ([SOURCE_CLASS_NAMES.STATUS, SOURCE_CLASS_NAMES.TYPE]?.includes(activeCol?.source_class)) {
      return <StatusButton status={{ ...row }} />;
    }

    return fetchValueByDotOperator(row, getStatusColKey(columnKey)) ? (
      <StatusButton isTruncate={true} status={fetchValueByDotOperator(row, getStatusColKey(columnKey))} />
    ) : (
      '-'
    );
  };

  const getArrayColValue = (row = {}, colKey = '') => {
    const arrVal = fetchValueByDotOperator(row, getStatusColKey(colKey));
    if (arrVal === 'Restricted') return '-';
    if ((arrVal || [])?.length > 0) {
      return (arrVal || []).map((val) => val?.name ?? val?.slug ?? val).join(', ');
    } else {
      return '-';
    }
  };

  const RenderUserRoles = ({ roles }) => {
    const roleStyle = {
      backgroundColor: 'var(--user-primary-light)',
      color: 'var(--ant-color-primary)',
      borderRadius: '24px',
      padding: '4px 12px 4px 12px',
      height: '32px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    };
    return (
      <Flex wrap gap={4}>
        {roles?.map((role, index) => {
          return (
            <div key={index} className="body-12-medium" style={roleStyle}>
              {role}
            </div>
          );
        })}
      </Flex>
    );
  };

  const RenderText = ({ text, limit = 60 }) => {
    const displayText = text.length > limit ? text.slice(0, limit) + '...' : text;

    return (
      <div className="body-12-medium" data-tag="allowRowEvents" style={{ whiteSpace: 'pre-wrap' }}>
        {text.length > limit ? (
          <Tooltip
            title={text}
            overlayClassName={styles.tableTooltip}
            overlayInnerStyle={{
              color: 'var(--text-main)',
              backgroundColor: 'var(--user-dropdown-bg)',
              whiteSpace: 'pre-wrap',
              border: '1px solid var(--user-card-border)',
              borderRadius: 12,
              padding: '12px 14px',
              boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
            }}
          >
            <span>{displayText}</span>
          </Tooltip>
        ) : (
          <span>{text}</span>
        )}
      </div>
    );
  };

  const getColSelector = (activeCol = {}) => {
    const colKey = activeCol.key;
    switch (activeCol?.field_type) {
      case COLUMN_TYPE_SLUGS.STATUS:
      case COLUMN_TYPE_SLUGS.PILL:
        return (row) => fetchValueByDotOperator(row, getStatusColKey(colKey));
      default:
        return (row) => fetchValueByDotOperator(row, colKey);
    }
  };

  const getColSortFields = (acticeCol = {}) => {
    if (acticeCol?.sortable && acticeCol?.sortfield)
      return {
        sortable: true,
        sortField: acticeCol.sortfield,
      };
    return {};
  };

  const getColMinWidth = (activeCol = {}) => {
    if (activeCol?.min_width) {
      return { minWidth: `${activeCol.min_width}px` };
    } else {
      switch (activeCol?.field_type) {
        case COLUMN_TYPE_SLUGS.ARRAY:
          return { minWidth: '260px' };
        case COLUMN_TYPE_SLUGS.AVATAR_TEXT_PATIENT:
        case COLUMN_TYPE_SLUGS.AVATAR_TEXT_USER:
          return { minWidth: '250px' };
        case COLUMN_TYPE_SLUGS.TICKET_ASSIGNEE:
          return { minWidth: '260px' };
        case COLUMN_TYPE_SLUGS.STATUS:
        case COLUMN_TYPE_SLUGS.PILL:
        case COLUMN_TYPE_SLUGS.ICON_PILL:
          return { minWidth: '170px' };
        case COLUMN_TYPE_SLUGS.FILTERS:
          return { minWidth: '270px' };
        case COLUMN_TYPE_SLUGS.URL:
        case COLUMN_TYPE_SLUGS.DATETIME:
          return { minWidth: '170px' };
        case COLUMN_TYPE_SLUGS.PRODUCTS_ARRAY:
          return { minWidth: '600px' };

        default:
          return { minWidth: '150px' };
      }
    }
  };

  const getColCell = (activeCol = {}) => {
    const columnKey = activeCol?.key;
    const sortField = activeCol?.sortfield;

    const handleRowClick = (row, event) => {
      onRowClick(row, event);
    };

    const profile_pic_columnKey = columnKey?.split('.').slice(0, -1).concat(['profile_picture']).join('.');
    const profile_email_columnKey = columnKey?.split('.').slice(0, -1).concat(['email']).join('.');
    const profile_pic_avatarKey = columnKey?.split('.').slice(0, -1).concat(['avatar_url']).join('.');
    const userId = columnKey?.split('.').slice(0, -1).concat(['id']).join('.');

    switch (activeCol?.field_type) {
      case COLUMN_TYPE_SLUGS.AVATAR_TEXT_USER:
      case COLUMN_TYPE_SLUGS.AVATAR_TEXT_PATIENT:
        return (row) => {
          const isSystemUser = activeCol?.field_type === COLUMN_TYPE_SLUGS.AVATAR_TEXT_USER;
          const isUserExist = fetchValueByDotOperator(row, userId);

          return (
            <div onClick={(event) => (isSystemUser ? null : handleRowClick(row, event))}>
              {isUserExist ? (
                <Flex align="center" gap={12}>
                  <Avatar
                    src={
                      fetchValueByDotOperator(row, profile_pic_columnKey) ||
                      fetchValueByDotOperator(row, profile_pic_avatarKey)
                    }
                    size={40}
                    name={fetchValueByDotOperator(row, columnKey)}
                    isSystem={isSystemUser}
                  />
                  <Flex vertical>
                    <span className="body-14-medium">{fetchValueByDotOperator(row, columnKey) || 'No Name'}</span>
                    {/* <span style={{ color: 'var(--user-main-text)' }} className="body-12-regular">
                      {fetchValueByDotOperator(row, profile_email_columnKey)}
                    </span> */}
                  </Flex>
                </Flex>
              ) : (
                '-'
              )}
            </div>
          );
        };
      case COLUMN_TYPE_SLUGS.TICKET_ASSIGNEE:
        return (row) => <ChangeAssignee ticketData={row} onSuccessResolve={() => fetch()} />;
      case COLUMN_TYPE_SLUGS.SHORT_TEXT_COPY:
      case COLUMN_TYPE_SLUGS.TEXT_COPY:
        return (row) => (
          <div data-tag="allowRowEvents" style={{ maxWidth: '100%', overflow: 'hidden' }}>
            {fetchValueByDotOperator(row, columnKey) ? (
              <Flex align="center">
                <CopyToClipboard value={fetchValueByDotOperator(row, columnKey)} />
              </Flex>
            ) : (
              '-'
            )}
          </div>
        );
      case COLUMN_TYPE_SLUGS.TEXT:
        return (row) => {
          const text = fetchValueByDotOperator(row, columnKey) ? fetchValueByDotOperator(row, columnKey) : '-';
          return <RenderText text={text} />;
        };
      case COLUMN_TYPE_SLUGS.HOVER_TEXT_COPY:
        return (row) => {
          const text = fetchValueByDotOperator(row, columnKey) ? fetchValueByDotOperator(row, columnKey) : '-';
          return <RenderHoverCopyText text={text} heading={activeCol?.name} />;
        };

      case COLUMN_TYPE_SLUGS.TOGGLE:
        return (row) => (
          <div data-tag="allowRowEvents" style={{ whiteSpace: 'pre-wrap' }}>
            <IF condition={!extraActionDisabled} ELSE={fetchValueByDotOperator(row, columnKey) ? 'Yes' : 'No'}>
              <Switch
                disabled={extraActionLoading}
                checked={fetchValueByDotOperator(row, columnKey)}
                handleChange={() => extraAction(row?.id, fetchValueByDotOperator(row, columnKey), columnKey, row)}
              />
            </IF>
          </div>
        );
      case COLUMN_TYPE_SLUGS.DEACTIVATE_USER_TOGGLE:
        return (row) => <DeactivateUserToggle row={row} onSuccess={() => fetch()} />;
      case COLUMN_TYPE_SLUGS.BOOLEAN:
        return (row) => (
          <div data-tag="allowRowEvents" style={{ whiteSpace: 'pre-wrap' }}>
            {fetchValueByDotOperator(row, columnKey) ? 'Yes' : 'No'}
          </div>
        );
      case COLUMN_TYPE_SLUGS.STATUS:
      case COLUMN_TYPE_SLUGS.ICON_PILL:
        return (row) => {
          const val = fetchValueByDotOperator(row, columnKey);
          if (ORDER_TYPE_ICONS?.[val?.slug]) {
            return (
              <div data-tag="allowRowEvents">
                <Flex gap={8} align="center" className={styles.iconPill}>
                  {ORDER_TYPE_ICONS?.[val?.slug]}
                  <div className="body-12-medium">{val?.name}</div>
                </Flex>
              </div>
            );
          }
          return <div data-tag="allowRowEvents">{getStatusColValue(activeCol, row, columnKey)}</div>;
        };
      case COLUMN_TYPE_SLUGS.PILL:
        return (row) => <div data-tag="allowRowEvents">{getStatusColValue(activeCol, row, columnKey)}</div>;
      case COLUMN_TYPE_SLUGS.MULTI_PILL:
        return (row) => <RenderMultiPills pills={fetchValueByDotOperator(row, columnKey)} />;
      case COLUMN_TYPE_SLUGS.ARRAY:
        return (row) => {
          const text = getArrayColValue(row, columnKey);
          if (sortField === 'roles') return <RenderUserRoles roles={text?.split(', ')} />;
          return <RenderText text={text} limit={65} />;
        };
      case COLUMN_TYPE_SLUGS.AVATAR_MULTIPLE_USER:
        return (row) => {
          return <RenderMultipleUser data={fetchValueByDotOperator(row, columnKey)} />;
        };
      case COLUMN_TYPE_SLUGS.FILTERS:
        return (row) => {
          return <RenderMultipleFilters data={fetchValueByDotOperator(row, columnKey)} />;
        };
      case COLUMN_TYPE_SLUGS.URL:
        return (row) => {
          const value = fetchValueByDotOperator(row, columnKey);
          if (!value) return '-';

          return (
            <Tooltip title={value}>
              <Flex align="center">
                <a
                  className={`${styles.url} ${styles.truncate}`}
                  href={value}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {value}
                </a>
                <CopyToClipboard value={value} />
              </Flex>
            </Tooltip>
          );
        };
      case COLUMN_TYPE_SLUGS.JSON:
        return (row) => {
          const payloadObj = fetchValueByDotOperator(row, columnKey);
          if (!payloadObj || isEmptyObject(payloadObj)) return '-';
          return <RenderHoverCopyText text={payloadObj} heading={activeCol?.name} />;
        };
      case COLUMN_TYPE_SLUGS.DATE:
        return (row) => (
          <div data-tag="allowRowEvents">
            {fetchValueByDotOperator(row, columnKey) ? DisplayDate(fetchValueByDotOperator(row, columnKey)) : '-'}
          </div>
        );
      case COLUMN_TYPE_SLUGS.DATETIME:
        return (row) => (
          <div data-tag="allowRowEvents">
            {fetchValueByDotOperator(row, columnKey) ? DisplayDateTime(fetchValueByDotOperator(row, columnKey)) : '-'}
          </div>
        );
      case COLUMN_TYPE_SLUGS.PRODUCTS_ARRAY:
        return (row) => {
          const payloadObj = fetchValueByDotOperator(row, columnKey);
          if (!payloadObj || isEmptyArray(payloadObj)) return '-';
          return <RenderProductsArray data={payloadObj} />;
        };
      default:
        return (row) => {
          const text = fetchValueByDotOperator(row, columnKey) ? fetchValueByDotOperator(row, columnKey) : '-';
          return <RenderText text={text} />;
        };
    }
  };

  const getColHeader = (activeCol = {}) => {
    return (
      <Flex align="center" gap={4} className={styles.header}>
        <Tooltip
          title={activeCol?.description ? '' : activeCol?.name}
          color="var(--user-dropdown-bg)"
          overlayClassName={styles.infoTooltip}
        >
          <span className={styles.truncateHead}>{activeCol?.name}</span>
        </Tooltip>
        <ColumnInfo name={activeCol?.name} description={activeCol?.description} />
      </Flex>
    );
  };

  const getColData = (activeCol = {}) => {
    return {
      name: getColHeader(activeCol),
      selector: getColSelector(activeCol),
      cell: getColCell(activeCol),
      key: activeCol?.key,
      ...getColMinWidth(activeCol),
      ...getColSortFields(activeCol),
    };
  };

  const filterActiveColumns = (activeCols = []) => {
    const filteredColumns = [];
    if (activeCols?.length) {
      activeCols?.forEach((activeCol) => {
        if (activeCol?.active && activeCol?.listable) {
          filteredColumns.push(getColData(activeCol));
        }
      });
    }

    const actionColumn = getActions(actionsList, renderActionsDirectly);

    if (!actionColumn) return filteredColumns;

    return [...filteredColumns, actionColumn];
  };

  useEffect(() => {
    if (activeColumns?.length) {
      setTableColumns(filterActiveColumns(activeColumns));
    }
  }, [activeColumns, extraActionLoading]);

  return {
    tableColumns,
  };
};

export default useTableColumns;
